import { Button } from '@/components/ui/button'

/**
 * A bar that sticks to the bottom of the screen while a form has unsaved changes, so Save is always in reach however
 * long the form is. It shows a brief "Saved" confirmation afterwards and says why a save failed.
 *
 * Inside a form it submits that form. Where there is no form, pass <c>onSave</c> instead.
 */
export default function UnsavedBar({ visible, saved, error, isPending, onCancel, onSave }: {
  /** There are unsaved changes. */
  visible: boolean
  /** The last save just worked: shows the confirmation when there is nothing unsaved. */
  saved?: boolean
  /** Why the last save failed, if it did. */
  error?: string | null
  isPending: boolean
  onCancel: () => void
  onSave?: () => void
}) {
  if (!visible) {
    if (!saved) return null
    return (
      <div role="status" className="sticky bottom-4 z-10 flex items-center rounded-lg border border-green-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
        <p className="text-sm font-medium text-green-700">Saved</p>
      </div>
    )
  }

  return (
    <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
      {error
        ? <p className="text-sm font-medium text-red-600" role="alert">{error}</p>
        : <p className="text-sm font-medium text-amber-700">You have unsaved changes</p>}
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isPending}>Discard</Button>
        <Button type={onSave ? 'button' : 'submit'} size="sm" onClick={onSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save changes'}</Button>
      </div>
    </div>
  )
}
